import { useEffect, useState } from "react";
import style from "./pdf-style.module.scss";
import Overview from "./components/Overview";
import Header from "./components/Header";
import TableDetails from "./components/TableDetails";
import {
  borderIncomeBuilderUI,
  detailsBuilderUI,
  liabilityBuilderUI,
  outgoingCCBuilderUI,
  expensesBuilderUI,
  rentalIncomeBuilderUI,
  salaryIncomeBuilderUI,
  salaryOtherIncomeBuilderUI,
  generateLoanAmountApproval,
  reportBuilderUI,
  loanBuilderUI,
  loansFilterBySecurity,
} from "./lib/builder";
import SectionTitle from "./components/SectionTitle";
import TableList from "./components/TableList";
import LenderTable from "./components/LenderTable";
import LenderTableList from "./components/LenderTableList";
import SecurityDetails from "./components/SecurityDetails";
import { IPdfReport } from "./models/report";
import { LoanAmountApprovalBar } from "../../result";
import { LiabilityTypeCreditCardsData } from "../../liabilities";
import { twMerge } from "tailwind-merge";

// import data from "./sample-data/full-data.json";

declare global {
  interface Window {
    injectReportData: any;
  }
}

const PdfGenerator = () => {
  const [report, setReport] = useState<IPdfReport>();
  const [loanAmountApproval, setLoanAmountApproval] = useState<any>();
  const [activeLenders, setActiveLenders] = useState<any>();
  const [isMultipleLoans, setIsMultipleLoans] = useState(false);

  useEffect(() => {
    window.injectReportData = (data: IPdfReport) => {
      const reportData = reportBuilderUI(data);
      if (reportData?.resReportData && reportData?.resReportData?.report?.id) {
        const _loanAmountApproval = generateLoanAmountApproval(
          reportData.resReportData,
        );
        setLoanAmountApproval(_loanAmountApproval);
        setActiveLenders(
          _loanAmountApproval.find(
            (_loan: any) => _loan.LoanAmount === reportData?.currentLoanAmount,
          ),
        );
        setIsMultipleLoans(
          reportData?.resReportData?.report?.cases?.length === 1,
        );
      }
      setReport(reportData);
    };
  }, []);

  let sectionCount = 1;
  const hasOutgoing =
    Boolean(
      report?.reqReportData?.details?.applicantParty?.find(
        (_exp: any) => _exp.expense?.length,
      )?.applicant?.length,
    ) ||
    Boolean(
      report?.reqReportData?.details?.applicantParty?.find(
        (_exp: any) => _exp.liability?.length,
      )?.applicant?.length,
    );

  return (
    <div className={style["pdf-wrapper"]}>
      <div className={style["pdf-container"]}>
        <Header requestId={report?.reportId} />

        <SectionTitle no={sectionCount++} title="Overview" />
        {!isMultipleLoans && loanAmountApproval?.length > 0 && (
          <>
            <Overview
              loanAmount={report?.currentLoanAmount ?? 0}
              lvr={report?.resReportData?.report?.accuracy ?? 0}
            />
            <div
              style={{
                marginBottom: "20px",
                height: "200px",
                width: "850px",
                margin: "0 auto",
              }}
              className={style["bar-chart"]}
            >
              <LoanAmountApprovalBar
                loanAmountApproval={loanAmountApproval}
                activeLenders={activeLenders}
                onBarClick={() => {}}
                isForPDF={true}
              />
            </div>
          </>
        )}
        {activeLenders?.Pass?.filter((_result: any) =>
          report?.lenders.includes(_result?.lender?.name),
        )?.length > 0 && (
          <LenderTableList
            isApprove={true}
            list={activeLenders?.Pass}
            lenders={report?.lenders ?? []}
          />
        )}
        {activeLenders?.Fail?.filter((_result: any) =>
          report?.lenders.includes(_result?.lender?.name),
        )?.length > 0 && (
          <LenderTableList
            list={activeLenders?.Fail}
            lenders={report?.lenders ?? []}
          />
        )}

        <SectionTitle no={sectionCount++} title="Your Details" />
        {report?.reqReportData.details?.applicantParty?.map(
          (_item: any, idx: number) => (
            <TableDetails
              title={`Applicant Details`}
              data={detailsBuilderUI(_item)}
              key={`details-${idx}`}
            />
          ),
        )}

        <SectionTitle
          no={sectionCount++}
          title="Security"
          noSpaceBottom={true}
        />
        {report?.reqReportData.details?.security?.map(
          (_item: any, idx: number) => (
            <>
              <SecurityDetails
                title={`Security ${idx + 1}`}
                security={_item}
                loans={report?.reqReportData.details?.loan}
                loanSecurityMapping={
                  report?.reqReportData.details?.loanSecurityMapping ?? []
                }
                key={`security-${idx}`}
              />
              {(
                loansFilterBySecurity(
                  report?.reqReportData.details?.loan,
                  _item,
                  report?.reqReportData.details?.loanSecurityMapping ?? [],
                ) || []
              )?.map((_item: any, idx: number) => (
                <TableDetails
                  title={`Linked Loan ${idx + 1}`}
                  data={loanBuilderUI(_item)}
                  key={`security-${idx}`}
                  noSpaceBottom={true}
                  isLoanDetails={true}
                  lendingAction={
                    _item?.loanType === "New"
                      ? "New"
                      : _item?.lendingAction === "TopUp"
                      ? "Top-Up"
                      : _item?.lendingAction !== "None"
                      ? _item?.lendingAction || ""
                      : ""
                  }
                />
              ))}
            </>
          ),
        )}

        <SectionTitle no={sectionCount++} title="Income" />
        {report?.reqReportData.details?.applicantParty?.map(
          (applicant: any, idx: number) => (
            <TableList
              title={`Salary / Wages`}
              column={[
                {
                  title: "Applicant",
                  key: "applicant",
                },
                {
                  title: "Type",
                  key: "type",
                },
                {
                  title: "Amount",
                  key: "amount",
                  align: "right",
                },
                {
                  title: "Kiwisaver",
                  key: "kiwisaver",
                },
              ]}
              data={salaryIncomeBuilderUI(
                applicant.income.filter(
                  (_i: any) =>
                    _i.incomeType === "SalaryWages" ||
                    _i.incomeType === "ForeignIncome",
                ),
                applicant.applicant,
              )}
              key={`salary-${idx}`}
              style={{ marginBottom: "20px" }}
            />
          ),
        )}
        {report?.reqReportData.details?.applicantParty?.map(
          (applicant: any) => {
            return applicant.income
              .filter((_i: any) => _i.incomeType === "RentalIncome")
              .map((_item: any, idx: number) => (
                <TableDetails
                  title={`Rental Income ${idx + 1}`}
                  data={rentalIncomeBuilderUI(_item, applicant.applicant)}
                  key={`rental-${idx}`}
                />
              ));
          },
        )}
        {report?.reqReportData.details?.applicantParty?.map(
          (applicant: any) => {
            return applicant.income
              .filter((_i: any) => _i.incomeType === "BoarderIncome")
              .map((_item: any, idx: number) => (
                <TableDetails
                  title={`Boarder Income ${idx + 1}`}
                  data={borderIncomeBuilderUI(_item, applicant.applicant)}
                  key={`border-${idx}`}
                />
              ));
          },
        )}
        {report?.reqReportData.details?.applicantParty?.map(
          (applicant: any, idx: number) => (
            <>
              {applicant.income.filter(
                (_i: any) =>
                  _i.incomeType !== "BoarderIncome" &&
                  _i.incomeType !== "RentalIncome" &&
                  _i.incomeType !== "SalaryWages" &&
                  _i.incomeType !== "ForeignIncome",
              ).length > 0 && (
                <TableList
                  title={`Other Income`}
                  column={[
                    {
                      title: "Applicant",
                      key: "applicant",
                    },
                    {
                      title: "Type",
                      key: "type",
                    },
                    {
                      title: "GrossAmount",
                      key: "amount",
                      align: "right",
                    },
                  ]}
                  data={salaryOtherIncomeBuilderUI(
                    applicant.income.filter(
                      (_i: any) =>
                        _i.incomeType !== "BoarderIncome" &&
                        _i.incomeType !== "RentalIncome" &&
                        _i.incomeType !== "SalaryWages" &&
                        _i.incomeType !== "ForeignIncome",
                    ),
                    applicant.applicant,
                  )}
                  key={`other-${idx}`}
                  style={{ marginBottom: "20px" }}
                />
              )}
            </>
          ),
        )}

        {hasOutgoing && (
          <>
            <SectionTitle no={sectionCount++} title="Outgoings" />
            {report?.reqReportData.details?.applicantParty?.map(
              (applicant: any, idx: number) => (
                <>
                  {applicant.liability.filter((_i: any) => !_i.repaymentAmount)
                    .length > 0 && (
                    <TableList
                      title={`Credit Cards`}
                      column={[
                        {
                          title: "Type",
                          key: "type",
                        },
                        {
                          title: "Limit",
                          key: "limit",
                          align: "right",
                        },
                        {
                          title: "Lender",
                          key: "lender",
                        },
                      ]}
                      data={outgoingCCBuilderUI(
                        applicant.liability.filter((_item: any) =>
                          LiabilityTypeCreditCardsData.find(
                            (_i: any) =>
                              _i["LiabilityType.Code"] === _item.liabilityType,
                          ),
                        ),
                      )}
                      key={`cc-${idx}`}
                      style={{ marginBottom: "20px" }}
                    />
                  )}
                </>
              ),
            )}

            {report?.reqReportData.details?.applicantParty?.map(
              (applicant: any, idx: number) => (
                <>
                  <TableList
                    title={`Expenses`}
                    column={[]}
                    data={[]}
                    key={`expenses-${idx}`}
                  />
                  {Object.keys(
                    applicant.expense.reduce((acc: any, _exp: any) => {
                      if (!acc[_exp.group]) {
                        acc[_exp.group] = [];
                      }
                      acc[_exp.group].push({ ..._exp });

                      return acc;
                    }, {}),
                  ).map((_item: any, _idx: number) => (
                    <TableList
                      title={``}
                      column={[
                        {
                          title: _item,
                          key: "type",
                        },
                        {
                          title: "Amount",
                          key: "amount",
                          align: "right",
                        },
                      ]}
                      data={expensesBuilderUI(applicant.expense, _item)}
                      key={`${_item}-${_idx}`}
                    />
                  ))}
                </>
              ),
            )}

            <div style={{ marginTop: "10px" }}>
              {report?.reqReportData.details?.applicantParty?.map(
                (applicant: any) => {
                  return applicant.liability
                    .filter(
                      (_item: any) =>
                        !LiabilityTypeCreditCardsData.find(
                          (_i: any) =>
                            _i["LiabilityType.Code"] === _item.liabilityType,
                        ),
                    )
                    .map((_item: any, idx: number) => (
                      <TableDetails
                        title={`Liabilities ${idx + 1}`}
                        data={liabilityBuilderUI(_item)}
                        key={`liability-${idx}`}
                      />
                    ));
                },
              )}
            </div>
          </>
        )}

        {activeLenders?.Pass?.filter((_result: any) =>
          report?.lenders.includes(_result?.lender?.name),
        )?.length > 0 && (
          <table
            className={twMerge(style["avoid-break"])}
            style={{ width: "100%", border: 0 }}
          >
            <tbody
              className={twMerge(style["avoid-break"])}
              style={{ border: 0 }}
            >
              <tr
                className={twMerge(style["avoid-break"])}
                style={{ border: 0 }}
              >
                <td
                  className={twMerge(style["avoid-break"])}
                  style={{ border: 0 }}
                >
                  {activeLenders?.Pass?.filter((_result: any) =>
                    report?.lenders.includes(_result?.lender?.name),
                  )?.map((lender: any, idx: number) => {
                    return (
                      <LenderTable
                        data={lender}
                        sectionNo={idx > 0 ? sectionCount : sectionCount++}
                        type={"Approve"}
                        lenderIdx={idx}
                        key={idx}
                        includeWarningsErrors={Boolean(
                          report?.includeWarningsErrors,
                        )}
                      />
                    );
                  })}
                </td>
              </tr>
            </tbody>
          </table>
        )}

        {activeLenders?.Fail?.filter((_result: any) =>
          report?.lenders.includes(_result?.lender?.name),
        )?.length > 0 && (
          <table
            className={twMerge(style["avoid-break"])}
            style={{ width: "100%", border: 0 }}
          >
            <tbody
              className={twMerge(style["avoid-break"])}
              style={{ border: 0 }}
            >
              <tr
                className={twMerge(style["avoid-break"])}
                style={{ border: 0 }}
              >
                <td
                  className={twMerge(style["avoid-break"])}
                  style={{ border: 0 }}
                >
                  {activeLenders?.Fail?.filter((_result: any) =>
                    report?.lenders.includes(_result?.lender?.name),
                  )?.map((lender: any, idx: number) => {
                    return (
                      <LenderTable
                        data={lender}
                        sectionNo={idx > 0 ? sectionCount : sectionCount++}
                        type={"Decline"}
                        lenderIdx={idx}
                        key={idx}
                        includeWarningsErrors={Boolean(
                          report?.includeWarningsErrors,
                        )}
                      />
                    );
                  })}
                </td>
              </tr>
            </tbody>
          </table>
        )}

        {report?.notes && (
          <table style={{ width: "100%", border: 0 }}>
            <tbody style={{ border: 0 }}>
              <tr style={{ border: 0 }}>
                <td style={{ border: 0 }}>
                  <SectionTitle no={sectionCount++} title="Notes" />
                  <div className={style["lender-notes"]}>
                    <p>{report?.notes}</p>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default PdfGenerator;
